import type { ApiResponse, ApiError } from '@octery/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';
const AUTH_REFRESH_PATH = '/api/auth/refresh';

const NON_REFRESHABLE_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  AUTH_REFRESH_PATH,
]);

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookieEntry = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));

  if (!cookieEntry) {
    return null;
  }

  return decodeURIComponent(cookieEntry.slice(name.length + 1));
}

function createRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type RequestTraceContext = {
  correlationId: string;
};

class ApiClient {
  private refreshRequest: Promise<boolean> | null = null;

  private shouldAttemptRefresh(path: string): boolean {
    return !NON_REFRESHABLE_PATHS.has(path);
  }

  private attachTraceHeaders(
    headers: Record<string, string>,
    traceContext: RequestTraceContext,
  ) {
    headers['X-Request-Id'] = createRequestId();
    headers['X-Correlation-Id'] = traceContext.correlationId;
  }

  private attachCsrfHeader(headers: Record<string, string>, method?: string) {
    if (!method || ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
      return;
    }

    const csrfToken = getCookieValue('octery_csrf');
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  private async refreshSession(traceContext: RequestTraceContext): Promise<boolean> {
    const headers: Record<string, string> = {};
    this.attachTraceHeaders(headers, traceContext);
    this.attachCsrfHeader(headers, 'POST');

    if (!this.refreshRequest) {
      this.refreshRequest = fetch(`${API_BASE}${AUTH_REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers,
      })
        .then((response) => response.ok)
        .catch(() => false)
        .finally(() => {
          this.refreshRequest = null;
        });
    }

    return this.refreshRequest;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    retryOnAuth = true,
    traceContext: RequestTraceContext = { correlationId: createRequestId() },
  ): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };
    this.attachTraceHeaders(headers, traceContext);

    // Don't set Content-Type for FormData (browser sets it with boundary)
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    this.attachCsrfHeader(headers, options.method);

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers,
    });

    if (response.status === 401 && retryOnAuth && this.shouldAttemptRefresh(path)) {
      const refreshed = await this.refreshSession(traceContext);
      if (refreshed) {
        return this.request<T>(path, options, false, traceContext);
      }
    }

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        success: false,
        error: { code: `HTTP_${response.status}`, message: response.statusText },
      }));
      throw new ApiClientError(
        error.error.message,
        error.error.code,
        response.status,
      );
    }

    // Handle CSV or other non-JSON responses
    const contentType = response.headers.get('Content-Type');
    if (contentType && !contentType.includes('application/json')) {
      return response.text() as unknown as T;
    }

    const data: ApiResponse<T> = await response.json();
    return data.data;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export const api = new ApiClient();
